import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import "./TenantsPage.css";
import { API_BASE } from "../config";

/* -------------------- Helpers -------------------- */
async function readPayloadSafe(res) {
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  try {
    if (ct.includes("application/json")) return await res.json();
    const text = await res.text();
    return text ? { message: text } : null;
  } catch {
    return null;
  }
}

function pickErrorMessage(res, payload) {
  const msg =
    (payload && typeof payload === "object" && (payload.error || payload.message)) ||
    (typeof payload === "string" && payload) ||
    null;

  if (res.status === 401) return msg || "Non autorizzato. Effettua il login.";
  if (res.status === 403) return msg || "Permesso negato (solo developer).";
  if (res.status === 404) return msg || "Endpoint non trovato (404).";
  return msg || `Errore HTTP ${res.status}`;
}

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return String(value).slice(0, 10);
}

function fromDateInputToIso(value) {
  if (!value) return null;
  // "mezzo giorno" per ridurre i problemi di timezone su date-only
  const d = new Date(`${value}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function licenseBadge(tenant) {
  const status = String(tenant?.licenseStatus || "active").toLowerCase();
  const expires = tenant?.licenseExpiresAt ? new Date(tenant.licenseExpiresAt) : null;
  const now = new Date();

  const isExpiredByDate =
    expires && !Number.isNaN(expires.getTime()) && expires.getTime() < now.getTime();

  if (status === "expired" || isExpiredByDate) {
    return { cls: "badge badge--bad", label: "Scaduta" };
  }

  if (expires && !Number.isNaN(expires.getTime())) {
    const diffDays = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 14) return { cls: "badge badge--warn", label: `Scade tra ${diffDays}g` };
  }

  return { cls: "badge badge--ok", label: "Attiva" };
}

/* -------------------- Component -------------------- */
export default function TenantsPage({ token }) {
  const [tenants, setTenants] = useState([]);

  const [listLoading, setListLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // CREATE
  const [tenantName, setTenantName] = useState("");
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // create licenses
  const [licenseSeats, setLicenseSeats] = useState("10");
  const [licenseStatus, setLicenseStatus] = useState("active");
  const [licenseExpiresAt, setLicenseExpiresAt] = useState("");

  // EDIT inline
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    licenseSeats: "10",
    licenseStatus: "active",
    licenseExpiresAt: "",
  });
  const [saving, setSaving] = useState(false);

  // DELETE modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ✅ TENANT DETAIL MODAL
  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [tenantModalLoading, setTenantModalLoading] = useState(false);
  const [tenantModalError, setTenantModalError] = useState("");
  const [tenantModalSuccess, setTenantModalSuccess] = useState("");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailShowPwd, setDetailShowPwd] = useState(false);

  const [detailForm, setDetailForm] = useState({
    // tenant
    name: "",
    licenseSeats: "10",
    licenseStatus: "active",
    licenseExpiresAt: "",
    // admin
    adminId: "",
    adminUsername: "",
    adminRole: "admin",
    newAdminPassword: "",
  });

  const listAbortRef = useRef(null);

  const resetAlerts = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  const resetModalAlerts = useCallback(() => {
    setTenantModalError("");
    setTenantModalSuccess("");
  }, []);

  const canSubmitCreate = useMemo(() => {
    return (
      Boolean(token) &&
      tenantName.trim() &&
      adminUsername.trim() &&
      adminPassword &&
      !createLoading
    );
  }, [token, tenantName, adminUsername, adminPassword, createLoading]);

  const fetchTenants = useCallback(async () => {
    if (!token) return;

    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;

    setListLoading(true);
    resetAlerts();

    try {
      const res = await fetch(`${API_BASE}/api/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(pickErrorMessage(res, payload));

      setTenants(Array.isArray(payload) ? payload : []);
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Errore nel recupero dei tenant");
      setTenants([]);
    } finally {
      setListLoading(false);
    }
  }, [token, resetAlerts]);

  useEffect(() => {
    if (!token) return;
    fetchTenants();
    return () => listAbortRef.current?.abort();
  }, [token, fetchTenants]);

  /* -------------------- Create -------------------- */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!token) return;

    resetAlerts();

    const tName = tenantName.trim();
    const aUser = adminUsername.trim();
    const aPass = adminPassword;

    if (!tName || !aUser || !aPass) {
      setError("Compila tutti i campi richiesti.");
      return;
    }

    const seatsNum = Number(licenseSeats);
    const seats = Number.isFinite(seatsNum) && seatsNum >= 1 ? Math.floor(seatsNum) : 10;

    setCreateLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/tenants`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tenantName: tName,
          adminUsername: aUser,
          adminPassword: aPass,
          licenseSeats: seats,
          licenseStatus,
          licenseExpiresAt: licenseExpiresAt ? fromDateInputToIso(licenseExpiresAt) : null,
        }),
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(pickErrorMessage(res, payload));

      setTenantName("");
      setAdminUsername("");
      setAdminPassword("");
      setShowPassword(false);

      setLicenseSeats("10");
      setLicenseStatus("active");
      setLicenseExpiresAt("");

      setSuccess("Tenant creato con successo.");
      await fetchTenants();
    } catch (err) {
      setError(err?.message || "Errore durante la creazione del tenant");
    } finally {
      setCreateLoading(false);
    }
  };

  /* -------------------- Edit (inline) -------------------- */
  const startEdit = (t) => {
    resetAlerts();
    setEditingId(t.id);

    setEditForm({
      name: t.name ?? "",
      licenseSeats: String(t.licenseSeats ?? 10),
      licenseStatus: String(t.licenseStatus ?? "active"),
      licenseExpiresAt: toDateInput(t.licenseExpiresAt),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    resetAlerts();

    const name = String(editForm.name || "").trim();
    if (!name) {
      setError("Il nome tenant è obbligatorio.");
      return;
    }

    const seatsNum = Number(editForm.licenseSeats);
    const seats = Number.isFinite(seatsNum) && seatsNum >= 1 ? Math.floor(seatsNum) : 1;

    const body = {
      name,
      licenseSeats: seats,
      licenseStatus: editForm.licenseStatus || "active",
      licenseExpiresAt: editForm.licenseExpiresAt
        ? fromDateInputToIso(editForm.licenseExpiresAt)
        : null,
    };

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/tenants/${editingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(pickErrorMessage(res, payload));

      setSuccess("Tenant aggiornato con successo.");
      setEditingId(null);
      await fetchTenants();
    } catch (err) {
      setError(err?.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  /* -------------------- Delete (modal) -------------------- */
  const openDeleteModal = (t) => {
    resetAlerts();
    setDeleteTarget(t);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    resetAlerts();
    setDeleteLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/tenants/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(pickErrorMessage(res, payload));

      setSuccess("Tenant eliminato con successo.");
      if (editingId === deleteTarget.id) setEditingId(null);

      setDeleteTarget(null);
      await fetchTenants();
    } catch (err) {
      setError(err?.message || "Errore durante l'eliminazione.");
    } finally {
      setDeleteLoading(false);
    }
  };

  /* =========================================================
     ✅ TENANT DETAIL MODAL
     ========================================================= */

  const closeTenantModal = useCallback(() => {
    if (detailSaving || tenantModalLoading) return;
    setTenantModalOpen(false);
    setTenantModalError("");
    setTenantModalSuccess("");
    setSelectedTenant(null);
    setTenantDetail(null);
    setDetailSaving(false);
    setDetailShowPwd(false);
    setDetailForm({
      name: "",
      licenseSeats: "10",
      licenseStatus: "active",
      licenseExpiresAt: "",
      adminId: "",
      adminUsername: "",
      adminRole: "admin",
      newAdminPassword: "",
    });
  }, [detailSaving, tenantModalLoading]);

  // ESC per chiudere
  useEffect(() => {
    if (!tenantModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeTenantModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [tenantModalOpen, closeTenantModal]);

  const openTenantModal = useCallback(
    async (t) => {
      if (!token || !t?.id) return;

      setTenantModalOpen(true);
      resetModalAlerts();
      setTenantModalLoading(true);
      setSelectedTenant(t);

      // prefill minimo da lista
      setDetailForm((p) => ({
        ...p,
        name: t.name ?? "",
        licenseSeats: String(t.licenseSeats ?? 10),
        licenseStatus: String(t.licenseStatus ?? "active"),
        licenseExpiresAt: toDateInput(t.licenseExpiresAt),
        adminId: String(t.adminId ?? ""),
        adminUsername: String(t.adminUsername ?? ""),
        adminRole: "admin",
        newAdminPassword: "",
      }));

      try {
        const res = await fetch(`${API_BASE}/api/tenants/${t.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const payload = await readPayloadSafe(res);

        // Se 404: endpoint non presente -> ok, restiamo con dati lista
        if (!res.ok) {
          if (res.status !== 404) throw new Error(pickErrorMessage(res, payload));
          setTenantDetail(null);
          return;
        }

        const detail = payload && typeof payload === "object" ? payload : null;
        setTenantDetail(detail);

        const admin =
          detail?.admin ||
          detail?.adminUser ||
          detail?.user ||
          (Array.isArray(detail?.users)
            ? detail.users.find((u) => String(u?.role).toLowerCase() === "admin")
            : null) ||
          null;

        setDetailForm((p) => ({
          ...p,
          name: detail?.name ?? p.name,
          licenseSeats: String(detail?.licenseSeats ?? p.licenseSeats),
          licenseStatus: String(detail?.licenseStatus ?? p.licenseStatus),
          licenseExpiresAt: toDateInput(detail?.licenseExpiresAt ?? p.licenseExpiresAt),
          adminId: String(detail?.adminId ?? admin?.id ?? p.adminId ?? ""),
          adminUsername: String(admin?.username ?? admin?.email ?? p.adminUsername ?? ""),
          adminRole: String(admin?.role ?? p.adminRole ?? "admin"),
          newAdminPassword: "",
        }));
      } catch (e) {
        setTenantModalError(e?.message || "Errore nel caricamento del dettaglio tenant.");
      } finally {
        setTenantModalLoading(false);
      }
    },
    [token, resetModalAlerts]
  );

  const saveTenantModal = async () => {
    if (!token) return;
    const tenantId = selectedTenant?.id;
    if (!tenantId) return;

    resetModalAlerts();

    const name = String(detailForm.name || "").trim();
    if (!name) {
      setTenantModalError("Il nome tenant è obbligatorio.");
      return;
    }

    const seatsNum = Number(detailForm.licenseSeats);
    const seats = Number.isFinite(seatsNum) && seatsNum >= 1 ? Math.floor(seatsNum) : 1;

    const tenantBody = {
      name,
      licenseSeats: seats,
      licenseStatus: detailForm.licenseStatus || "active",
      licenseExpiresAt: detailForm.licenseExpiresAt
        ? fromDateInputToIso(detailForm.licenseExpiresAt)
        : null,
    };

    const adminId = String(detailForm.adminId || "").trim();
    const adminUsernameNew = String(detailForm.adminUsername || "").trim();
    const adminRoleNew = String(detailForm.adminRole || "").trim();
    const newPwd = String(detailForm.newAdminPassword || "");

    setDetailSaving(true);

    try {
      // 1) update tenant
      {
        const res = await fetch(`${API_BASE}/api/tenants/${tenantId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(tenantBody),
        });

        const payload = await readPayloadSafe(res);
        if (!res.ok) throw new Error(pickErrorMessage(res, payload));
      }

      // 2) update admin (opzionale)
      const adminUpdateBody = {};
      if (adminUsernameNew) adminUpdateBody.username = adminUsernameNew;
      if (adminRoleNew) adminUpdateBody.role = adminRoleNew;
      if (newPwd) adminUpdateBody.password = newPwd;

      const shouldUpdateAdmin = Object.keys(adminUpdateBody).length > 0;

      if (shouldUpdateAdmin) {
        // tentativo 1: /api/tenants/:id/admin
        let adminRes = await fetch(`${API_BASE}/api/tenants/${tenantId}/admin`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(adminUpdateBody),
        });

        if (!adminRes.ok) {
          const p = await readPayloadSafe(adminRes);

          // fallback: /api/users/:adminId
          if (adminRes.status === 404 && adminId) {
            adminRes = await fetch(`${API_BASE}/api/users/${adminId}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(adminUpdateBody),
            });

            const p2 = await readPayloadSafe(adminRes);
            if (!adminRes.ok) throw new Error(pickErrorMessage(adminRes, p2));
          } else {
            throw new Error(pickErrorMessage(adminRes, p));
          }
        }
      }

      setTenantModalSuccess("Tenant (e admin) aggiornati con successo.");
      setDetailForm((p) => ({ ...p, newAdminPassword: "" }));
      await fetchTenants();
    } catch (e) {
      setTenantModalError(e?.message || "Errore durante il salvataggio.");
    } finally {
      setDetailSaving(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="page tenants-page">
        <div className="tenants-page__header">
          <h2 className="tenants-page__title">Gestione Tenant</h2>

          <button
            type="button"
            className="btn btn--ghost"
            onClick={fetchTenants}
            disabled={!token || listLoading}
            title={!token ? "Effettua il login" : "Aggiorna elenco tenant"}
          >
            <span aria-hidden>↻</span> {listLoading ? "Aggiorno…" : "Aggiorna"}
          </button>
        </div>

        {!token && (
          <div className="alert alert--info" role="status">
            Per visualizzare i tenant effettua l’accesso.
          </div>
        )}

        {(error || success) && (
          <div
            className={`alert ${error ? "alert--error" : "alert--success"}`}
            role={error ? "alert" : "status"}
          >
            {error || success}
          </div>
        )}

        {/* CREATE */}
        <section className="tx-card">
          <div className="card-head">
            <h3 className="card-title">Crea nuovo tenant</h3>
          </div>

          <form onSubmit={handleCreate} className="tenants-grid">
            <div className="field col-12">
              <label className="label" htmlFor="tenant-name">
                Nome tenant
              </label>
              <input
                id="tenant-name"
                className="input"
                value={tenantName}
                onChange={(e) => {
                  resetAlerts();
                  setTenantName(e.target.value);
                }}
                disabled={createLoading}
                placeholder="es. Circolo Bocciofilo"
                autoComplete="organization"
                required
              />
            </div>

            <div className="field col-6">
              <label className="label" htmlFor="admin-username">
                Username admin
              </label>
              <input
                id="admin-username"
                className="input"
                value={adminUsername}
                onChange={(e) => {
                  resetAlerts();
                  setAdminUsername(e.target.value);
                }}
                disabled={createLoading}
                placeholder="es. admin.circolo"
                autoComplete="username"
                required
              />
            </div>

            <div className="field col-6">
              <label className="label" htmlFor="admin-password">
                Password admin
              </label>

              <div className="password-row">
                <input
                  id="admin-password"
                  className="input"
                  type={showPassword ? "text" : "password"}
                  value={adminPassword}
                  onChange={(e) => {
                    resetAlerts();
                    setAdminPassword(e.target.value);
                  }}
                  disabled={createLoading}
                  placeholder="Inserisci una password"
                  autoComplete="new-password"
                  required
                />

                <button
                  type="button"
                  className="toggle-pill"
                  onClick={() => setShowPassword((s) => !s)}
                  disabled={createLoading}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                >
                  {showPassword ? "Nascondi" : "Mostra"}
                </button>
              </div>
            </div>

            <div className="field col-4">
              <label className="label" htmlFor="license-seats">
                Licenze (posti / max utenti)
              </label>
              <input
                id="license-seats"
                className="input"
                type="number"
                min="1"
                value={licenseSeats}
                onChange={(e) => {
                  resetAlerts();
                  setLicenseSeats(e.target.value);
                }}
                disabled={createLoading}
                placeholder="es. 10"
              />
            </div>

            <div className="field col-4">
              <label className="label" htmlFor="license-status">
                Stato licenza
              </label>
              <select
                id="license-status"
                className="select"
                value={licenseStatus}
                onChange={(e) => {
                  resetAlerts();
                  setLicenseStatus(e.target.value);
                }}
                disabled={createLoading}
              >
                <option value="active">active</option>
                <option value="expired">expired</option>
              </select>
            </div>

            <div className="field col-4">
              <label className="label" htmlFor="license-expires">
                Scadenza licenza
              </label>
              <input
                id="license-expires"
                className="input"
                type="date"
                value={licenseExpiresAt}
                onChange={(e) => {
                  resetAlerts();
                  setLicenseExpiresAt(e.target.value);
                }}
                disabled={createLoading}
              />
            </div>

            <div className="tenants-actions col-12">
              <button type="submit" className="btn btn--primary" disabled={!canSubmitCreate}>
                {createLoading ? "Creazione…" : "Crea tenant"}
              </button>
            </div>
          </form>
        </section>

        {/* LIST */}
        <section className="tx-card">
          <div className="card-head">
            <h3 className="card-title">Elenco tenant</h3>
            <span className="tx-muted" style={{ fontSize: "0.9rem" }}>
              {tenants.length} totali
            </span>
          </div>

          {listLoading ? (
            <p>Caricamento…</p>
          ) : !token ? (
            <p>Accedi per visualizzare l’elenco tenant.</p>
          ) : tenants.length === 0 ? (
            <p>Nessun tenant disponibile.</p>
          ) : (
            <>
              <div className="table-block">
                <table className="tenant-table">
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>ID</th>
                      <th>Nome</th>
                      <th style={{ width: 130 }}>Admin ID</th>
                      <th style={{ width: 320 }}>Licenza</th>
                      <th style={{ width: 160 }}>Azioni</th>
                    </tr>
                  </thead>

                  <tbody>
                    {tenants.map((t) => {
                      const isEditing = editingId === t.id;
                      const badge = licenseBadge(t);

                      return (
                        <tr
                          key={t.id}
                          className="tenant-row"
                          onClick={() => openTenantModal(t)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openTenantModal(t);
                            }
                          }}
                          title="Clicca per aprire dettagli / modifica"
                        >
                          <td className="mono">{t.id}</td>

                          <td>
                            {isEditing ? (
                              <input
                                className="input"
                                value={editForm.name}
                                onChange={(e) =>
                                  setEditForm((p) => ({ ...p, name: e.target.value }))
                                }
                                disabled={saving}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              t.name
                            )}
                          </td>

                          <td className="mono">{t.adminId ?? "—"}</td>

                          <td>
                            {isEditing ? (
                              <div
                                className="tenants-license-edit"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="field">
                                  <label className="label">Posti</label>
                                  <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={editForm.licenseSeats}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        licenseSeats: e.target.value,
                                      }))
                                    }
                                    disabled={saving}
                                  />
                                </div>

                                <div className="field">
                                  <label className="label">Stato</label>
                                  <select
                                    className="select"
                                    value={editForm.licenseStatus}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        licenseStatus: e.target.value,
                                      }))
                                    }
                                    disabled={saving}
                                  >
                                    <option value="active">active</option>
                                    <option value="expired">expired</option>
                                  </select>
                                </div>

                                <div className="field">
                                  <label className="label">Scadenza</label>
                                  <input
                                    className="input"
                                    type="date"
                                    value={editForm.licenseExpiresAt}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        licenseExpiresAt: e.target.value,
                                      }))
                                    }
                                    disabled={saving}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="tenants-license">
                                <span className={badge.cls}>{badge.label}</span>
                                <span className="tenants-license__meta">
                                  Posti: <strong>{t.licenseSeats ?? "—"}</strong>
                                  {" · "}
                                  Scadenza:{" "}
                                  <strong>
                                    {t.licenseExpiresAt ? toDateInput(t.licenseExpiresAt) : "—"}
                                  </strong>
                                </span>
                              </div>
                            )}
                          </td>

                          <td onClick={(e) => e.stopPropagation()}>
                            {isEditing ? (
                              <div className="tx-row" style={{ gap: 10 }}>
                                <button
                                  type="button"
                                  className="btn btn--primary"
                                  onClick={saveEdit}
                                  disabled={saving}
                                >
                                  ✓ {saving ? "Salvo…" : "Salva"}
                                </button>

                                <button
                                  type="button"
                                  className="btn btn--ghost"
                                  onClick={cancelEdit}
                                  disabled={saving}
                                >
                                  Annulla
                                </button>
                              </div>
                            ) : (
                              <div className="tx-row" style={{ gap: 10 }}>
                                <button
                                  type="button"
                                  className="btn btn--icon has-tooltip"
                                  data-tip="Modifica tenant (inline)"
                                  onClick={() => startEdit(t)}
                                  aria-label="Modifica tenant"
                                >
                                  ✏️
                                </button>

                                <button
                                  type="button"
                                  className="btn btn--icon btn--danger has-tooltip"
                                  data-tip="Elimina tenant"
                                  onClick={() => openDeleteModal(t)}
                                  disabled={!token}
                                  aria-label="Elimina tenant"
                                >
                                  🗑️
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="tx-muted tenants-note">
                <strong>Tip:</strong> clicca su una riga per aprire il pannello di dettaglio e
                modificare anche l’admin.
              </div>
            </>
          )}
        </section>

        {/* ✅ TENANT DETAIL MODAL */}
        {tenantModalOpen && (
          <div
            className="modal-overlay tenants-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-detail-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeTenantModal();
            }}
          >
            <div
              className="modal-content tenants-modal__content tenants-detailModal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="tenants-modal__head">
                <div>
                  <h3 id="tenant-detail-title" className="tenants-modal__title">
                    Dettaglio tenant
                  </h3>
                  <div className="tx-muted" style={{ fontWeight: 800 }}>
                    ID: <span className="mono">{selectedTenant?.id ?? "—"}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn--icon"
                  onClick={closeTenantModal}
                  aria-label="Chiudi"
                >
                  ✕
                </button>
              </div>

              {tenantModalLoading ? (
                <p>Caricamento dettagli…</p>
              ) : (
                <>
                  {(tenantModalError || tenantModalSuccess) && (
                    <div
                      className={`alert ${
                        tenantModalError ? "alert--error" : "alert--success"
                      }`}
                      role="status"
                    >
                      {tenantModalError || tenantModalSuccess}
                    </div>
                  )}

                  <div className="tenants-detailGrid">
                    {/* TENANT */}
                    <div className="tenants-detailSection">
                      <div className="tenants-detailSection__title">Tenant</div>

                      <div className="field">
                        <label className="label">Nome</label>
                        <input
                          className="input"
                          value={detailForm.name}
                          onChange={(e) =>
                            setDetailForm((p) => ({ ...p, name: e.target.value }))
                          }
                          disabled={detailSaving}
                        />
                      </div>

                      <div className="tenants-detailRow2">
                        <div className="field">
                          <label className="label">Licenze (posti)</label>
                          <input
                            className="input"
                            type="number"
                            min="1"
                            value={detailForm.licenseSeats}
                            onChange={(e) =>
                              setDetailForm((p) => ({
                                ...p,
                                licenseSeats: e.target.value,
                              }))
                            }
                            disabled={detailSaving}
                          />
                        </div>

                        <div className="field">
                          <label className="label">Stato</label>
                          <select
                            className="select"
                            value={detailForm.licenseStatus}
                            onChange={(e) =>
                              setDetailForm((p) => ({
                                ...p,
                                licenseStatus: e.target.value,
                              }))
                            }
                            disabled={detailSaving}
                          >
                            <option value="active">active</option>
                            <option value="expired">expired</option>
                          </select>
                        </div>
                      </div>

                      <div className="field">
                        <label className="label">Scadenza licenza</label>
                        <input
                          className="input"
                          type="date"
                          value={detailForm.licenseExpiresAt}
                          onChange={(e) =>
                            setDetailForm((p) => ({
                              ...p,
                              licenseExpiresAt: e.target.value,
                            }))
                          }
                          disabled={detailSaving}
                        />
                      </div>

                      {!!tenantDetail && (
                        <div className="tx-muted" style={{ fontSize: "0.88rem", fontWeight: 800 }}>
                          (Dettagli caricati da server)
                        </div>
                      )}
                    </div>

                    {/* ADMIN */}
                    <div className="tenants-detailSection">
                      <div className="tenants-detailSection__title">Admin</div>

                      <div className="tenants-detailRow2">
                        <div className="field">
                          <label className="label">Admin ID</label>
                          <input className="input mono" value={detailForm.adminId || "—"} disabled />
                        </div>

                        <div className="field">
                          <label className="label">Ruolo (opzionale)</label>
                          <select
                            className="select"
                            value={detailForm.adminRole}
                            onChange={(e) =>
                              setDetailForm((p) => ({ ...p, adminRole: e.target.value }))
                            }
                            disabled={detailSaving}
                          >
                            <option value="admin">admin</option>
                            <option value="segretario">segretario</option>
                            <option value="tesoriere">tesoriere</option>
                            <option value="developer">developer</option>
                            <option value="user">user</option>
                          </select>
                        </div>
                      </div>

                      <div className="field">
                        <label className="label">Username admin</label>
                        <input
                          className="input"
                          value={detailForm.adminUsername}
                          onChange={(e) =>
                            setDetailForm((p) => ({ ...p, adminUsername: e.target.value }))
                          }
                          disabled={detailSaving}
                          placeholder="es. admin.circolo"
                        />
                        <div className="tx-muted" style={{ fontSize: "0.85rem", fontWeight: 800 }}>
                          Se l’endpoint non supporta username, dimmelo e lo leghiamo al campo corretto
                          (email/username).
                        </div>
                      </div>

                      <div className="field">
                        <label className="label">Nuova password admin (opzionale)</label>
                        <div className="password-row">
                          <input
                            className="input"
                            type={detailShowPwd ? "text" : "password"}
                            value={detailForm.newAdminPassword}
                            onChange={(e) =>
                              setDetailForm((p) => ({ ...p, newAdminPassword: e.target.value }))
                            }
                            disabled={detailSaving}
                            placeholder="Lascia vuoto per non cambiarla"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="toggle-pill"
                            onClick={() => setDetailShowPwd((s) => !s)}
                            disabled={detailSaving}
                          >
                            {detailShowPwd ? "Nascondi" : "Mostra"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="tenants-modal__actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={closeTenantModal}
                      disabled={detailSaving}
                    >
                      Chiudi
                    </button>

                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={saveTenantModal}
                      disabled={detailSaving}
                    >
                      {detailSaving ? "Salvo…" : "Salva modifiche"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {deleteTarget && (
          <div
            className="modal-overlay tenants-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-tenant-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeDeleteModal();
            }}
          >
            <div className="modal-content tenants-modal__content">
              <h3 id="delete-tenant-title" className="tenants-modal__title">
                Eliminare tenant?
              </h3>

              <p className="tenants-modal__text">
                Stai per eliminare <strong>{deleteTarget.name}</strong>.
                <br />
                <span className="tenants-modal__warn">
                  Verranno eliminati anche utenti, fornitori, transazioni e ticket del tenant.
                </span>
              </p>

              <div className="tenants-modal__actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Annulla
                </button>

                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Elimino…" : "Sì, elimina"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
